import {
  SNOOZED_FEED_GROUP,
  UPDATES_FEED_SECTION,
  type FeedConfiguration,
} from '@littlebird/sdk';

import {
  layoutFeedGroups,
  type FeedGroupLeads,
} from '@/features/feed/feed-groups';
import { useFeedSections } from '@/features/feed/use-feed-sections';
import {
  EMPTY_FEED_FILTERS,
  useFeedItems,
} from '@/features/feed/use-feed-tabs';

import { HOME_SECTION_ROWS, needsYou } from './need-you-cards';

import type { WaitingForYouState } from './use-waiting-for-you';

// The Feed's For you view, newest arrival first
const HOME_FEED_CONFIGURATION: FeedConfiguration = {
  statuses: ['waiting', 'in_progress'],
  types: [],
  apps: [],
  groupBy: 'none',
  sort: 'newest',
};

// Updates and snoozed cards wait on the Feed only
const SECTIONS_LEFT_TO_THE_FEED = new Set([
  UPDATES_FEED_SECTION,
  SNOOZED_FEED_GROUP,
]);

// The Feed's sections as Home shows them. A section's own rows, such as the Feed composer's chats, come first and
// take some of its places
export const useHomeFeed = (
  home: WaitingForYouState,
  leads: FeedGroupLeads
) => {
  const { items, groupIds } = useFeedItems(
    home,
    EMPTY_FEED_FILTERS,
    HOME_FEED_CONFIGURATION
  );
  const sectionState = useFeedSections('home');

  let section: string | undefined;
  const shown = items.flatMap((item, index) => {
    section = groupIds[index] ?? section;
    return section !== undefined && SECTIONS_LEFT_TO_THE_FEED.has(section)
      ? []
      : [{ item, groupId: groupIds[index] }];
  });
  const shownGroupIds = shown.map(({ groupId }) => groupId);
  const layout = layoutFeedGroups({
    items: shown.map(({ item }) => item),
    groupIds: shownGroupIds,
    groupLabels: shownGroupIds,
    isCollapsed: sectionState.isCollapsed,
    leadGroupIds: Object.keys(leads),
    previewCount: (sectionId) =>
      HOME_SECTION_ROWS -
      Math.min(leads[sectionId]?.count ?? 0, HOME_SECTION_ROWS),
  });

  return {
    layout,
    // A done row that stays for the rest of the visit no longer needs the user
    needYouCount: shown.filter(
      ({ item }) =>
        item.kind === 'chat_prompt' || needsYou(item.entry.card.status)
    ).length,
    toggleSection: sectionState.toggleCollapsed,
  };
};

export type HomeFeed = ReturnType<typeof useHomeFeed>;
